import { useCallback, useContext, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import useLog from '../../useLog/useLog';
import useSwitchesFromContext from '../../../hooks/useSwitchesFromContext';
import CheckoutPageContext from '../../../context/CheckoutPageContext';
import $window from '../../../tools/window';
import { SHIP_MODE_TYPE } from '../../../tools/constants';

const getRandomBytes = (size) => {
  const buffer = new Uint8Array(size);
  crypto.getRandomValues(buffer);
  return Array.from(buffer).map((value) => value.toString(16)).join('');
};

const updateAddress = (target, src) => {
  const res = {};
  Object.keys(target).forEach((k) => {
    res[k] = (src[k] ?? target[k]);
  });
  return res;
};

const requestAddressVerification = ({ address, logger }) => {
  const requestID = getRandomBytes(16);
  const eventName = `addressVerification:mfe:response:${requestID}`;

  logger.log('requestAddressVerification', address);

  return new Promise((resolve, reject) => {
    const handleResponse = (event) => {
      const { error, data } = event.detail || {};

      if (error) {
        logger.log('verification error', error.message);
        reject(error);
      } else {
        logger.log('verification success', data);
        resolve({ ...data, requestID });
      }
    };

    $window.addEventListener(eventName, handleResponse, { once: true });

    $window.dispatchEvent(new CustomEvent('addressVerification:request', {
      detail: { address, requestID },
    }));
  });
};

/**
 * Custom hook to verify address using Checkout MFE
 */
const useAddressVerification = () => {
  const { reset, getValues } = useFormContext();
  const logger = useLog('checkout.addressVerification');
  const {
    setCheckoutPageState,
    checkoutPageState,
  } = useContext(CheckoutPageContext);
  const {
    cartPayment, bagItems, shipMethod, selectedPaymentType,
  } = checkoutPageState;
  const { getSwitchValue } = useSwitchesFromContext();
  const isCartVerified = getSwitchValue('isCartVerifiedEnabled');
  const isFeatureEnabled = getSwitchValue('chkAddrVerifyEnabled');

  const isHomeDelivery = useMemo(() => {
    if (!checkoutPageState?.shipMethod) return false;
    const { shipModeType } = checkoutPageState.shipMethod;
    return [
      SHIP_MODE_TYPE.HOME_DELIVERY,
      SHIP_MODE_TYPE.SAME_DAY_DELIVERY,
      SHIP_MODE_TYPE.DIRECT_TO_DESK,
    ].includes(shipModeType);
  }, [checkoutPageState?.shipMethod]);

  const verifyAddress = useCallback(async (address) => {
    logger.log('verifyAddress', address);

    if (!address) {
      logger.log('invalid address');
      return {
        address,
        editAddress: true,
        error: new Error('invalid address'),
        reason: 'invalid address',
      };
    }
    if (isCartVerified && address.isAddressVerified) {
      logger.log('address already verified');
      return {
        address,
        reason: 'address already verified',
        useEnteredAddress: true,
      };
    }

    if (cartPayment) {
      logger.log('express lane not supported');
      return {
        address,
        reason: 'not enabled',
        useEnteredAddress: true,
      };
    }

    if (!isFeatureEnabled) {
      logger.log('feature switch off');
      return {
        address,
        reason: 'not enabled',
        useEnteredAddress: true,
      };
    }

    if (['klarna', 'applepay', 'googlepay'].includes(selectedPaymentType)) {
      logger.log('unsupported payment method');
      return {
        address,
        reason: 'not enabled',
        useEnteredAddress: true,
      };
    }

    if (bagItems?.hasOnlyEGiftCard) {
      logger.log('eGiftCard only purchase not supported');
      return {
        address,
        reason: 'not enabled',
        useEnteredAddress: true,
      };
    }

    if (shipMethod?.shipModeType?.toLowerCase() === 'directtodesk') {
      logger.log('ship method not supported');
      return {
        address,
        reason: 'not enabled',
        useEnteredAddress: true,
      };
    }

    try {
      const result = await requestAddressVerification({ address, logger });
      return {
        ...result,
        address,
      };
    } catch (error) {
      logger.log('verification failed or MFE unavailable', error);
      return {
        address,
        error,
        reason: error.message || 'service unavailable',
        useEnteredAddress: true,
      };
    }
  }, [logger, isCartVerified, cartPayment, isFeatureEnabled,
    shipMethod?.shipModeType, selectedPaymentType, bagItems?.hasOnlyEGiftCard]);

  const triggerAddressVerification = async () => {
    if (!isHomeDelivery) {
      return true;
    }
    const values = getValues();
    const address = values?.addresses?.homeDelivery;
    const addressVerification = await verifyAddress(address);
    if (addressVerification.error) {
      logger.log('handleAddressVerification error', addressVerification.error);
      return true;
    }
    if (addressVerification.useEnteredAddress) {
      logger.log(
        'handleAddressVerification',
        'process payments for reason',
        addressVerification.reason,
      );
      return true;
    }
    if (addressVerification.useSuggestedAddress) {
      logger.log(
        'handleAddressVerification',
        'update address to suggested address for reason',
        addressVerification.reason,
      );
      reset((prev) => ({
        ...prev,
        addresses: {
          ...prev.addresses,
          homeDelivery: {
            ...prev.addresses.homeDelivery,
            ...addressVerification.suggestedAddress,
          },
        },
      }));
      const checkoutShippingAddress = {
        country: address?.country,
        addressId: address?.addressId,
        addressType: address?.addressType,
        address1: address?.address1,
        address2: address?.address2,
        city: address?.city,
        postalCode: address?.postalCode,
        state: address?.state,
        province: address?.province,
        displayType: address?.displayType,
        district: address?.district,
        firstName: address?.firstName,
        isAPOFPO: address?.isAPOFPO,
        isBillingAllowed: address?.isBillingAllowed,
        isDefaultBilling: address?.isDefaultBilling,
        isDefaultShipping: address?.isDefaultShipping,
        isShippingAllowed: address?.isShippingAllowed,
        lastName: address?.lastName,
        legendAddress: address?.legendAddress,
        legendName: address?.legendName,
        orgName: address?.orgName,
        pickUpStoreId: address?.pickUpStoreId,
        poBox: address?.poBox,
        sameDayStoreId: address?.sameDayStoreId,
        subType: address?.subType,
        title: address?.title,
        collectionPointID: address?.collectionPointID,
        isAddressVerified: true,
        email: address.email,
        phone: address.phone,
      };
      const newShippingAddress = updateAddress(
        checkoutShippingAddress,
        { ...addressVerification.suggestedAddress },
      );
      setCheckoutPageState((currentState) => ({
        ...currentState,
        shippingAddress: { ...newShippingAddress },
      }));
      $window.dispatchEvent(new CustomEvent('addressVerification:suggestedAddressFlag'));
      return true;
    }
    return false;
  };
  return {
    verifyAddress,
    triggerAddressVerification,
  };
};

export default useAddressVerification;
